/**
 * PocketBase Collection Types
 * Corresponds to production schema extracted from pb_schema.json
 * Last synced: 31. März 2026
 */

// Auth User (system auth collection)
export interface UserRecord {
  id: string;
  email: string;
  emailVisibility: boolean;
  verified: boolean;
  created: string;
  updated: string;
  // custom fields
  username?: string;
  avatar?: string;
  language?: 'de' | 'en' | 'nl' | 'es' | 'fr';
  theme?: 'light' | 'dark' | 'auto';
}

// Fitness Module
export interface BodyDataRecord {
  id: string;
  user: string; // relation to users
  date: string; // YYYY-MM-DD
  weight?: number; // kg
  height?: number; // cm
  bodyFatPercentage?: number;
  created: string;
  updated: string;
}

export interface ExerciseLogsRecord {
  id: string;
  user: string;
  date: string;
  exerciseType: string; // running, strength, yoga, etc.
  duration: number; // minutes
  intensity: 'low' | 'medium' | 'high';
  calories?: number;
  notes?: string;
  created: string;
  updated: string;
}

export interface DailyGoalsRecord {
  id: string;
  user: string;
  date: string;
  calorieGoal?: number;
  stepsGoal?: number;
  waterGoal?: number; // liters
  sleepGoal?: number; // hours
  exerciseMinutesGoal?: number;
  notes?: string;
  created: string;
  updated: string;
}

// Calendar Module
export interface CalendarEventsRecord {
  id: string;
  user: string;
  title: string;
  description?: string;
  startDate: string; // ISO datetime
  endDate: string;
  location?: string;
  category?: string; // personal, work, birthday, etc.
  color?: string; // hex color
  isAllDay?: boolean;
  reminders?: string[]; // array of reminder times before event
  created: string;
  updated: string;
}

export interface TimetableRecord {
  id: string;
  user: string;
  subject: string;
  dayOfWeek: number; // 0-6 (Mon-Sun)
  startTime: string; // HH:MM
  endTime: string;
  room?: string;
  instructor?: string;
  notes?: string;
  created: string;
  updated: string;
}

// School/Study Module
export interface HomeworkRecord {
  id: string;
  user: string;
  subject: string;
  title: string;
  description?: string;
  dueDate: string;
  priority: 'low' | 'medium' | 'high';
  completed: boolean;
  completedDate?: string;
  attachments?: string[]; // file ids
  notes?: string;
  created: string;
  updated: string;
}

export interface ExamsRecord {
  id: string;
  user: string;
  subject: string;
  title: string;
  date: string;
  time?: string;
  duration?: number; // minutes
  location?: string;
  weight?: number; // percentage for final grade
  notes?: string;
  created: string;
  updated: string;
}

export interface GradesRecord {
  id: string;
  user: string;
  subject: string;
  gradeType: string; // test, homework, participation, etc.
  value: number; // 0-100 or letter grade equivalent
  weight?: number; // percentage in final grade
  date: string;
  notes?: string;
  created: string;
  updated: string;
}

export interface CustomSubjectsRecord {
  id: string;
  user: string;
  name: string;
  color?: string; // hex color for UI
  icon?: string; // icon name/emoji
  order?: number; // for custom sorting
  notes?: string;
  created: string;
  updated: string;
}

export interface SubjectFilesRecord {
  id: string;
  user: string;
  subject: string;
  fileName: string;
  fileUrl: string; // PocketBase file URL
  fileType: string; // pdf, doc, image, etc.
  uploadedDate: string;
  notes?: string;
  created: string;
  updated: string;
}

// Tools Module: Flashcards
export interface FlashcardDecksRecord {
  id: string;
  user: string;
  name: string;
  description?: string;
  subject?: string;
  color?: string;
  cardCount?: number; // denormalized for perf
  lastStudied?: string;
  created: string;
  updated: string;
}

export interface FlashcardsRecord {
  id: string;
  user: string;
  deck: string; // relation to flashcard_decks
  front: string;
  back: string;
  difficulty: 'easy' | 'medium' | 'hard';
  timesReviewed?: number;
  lastReviewed?: string;
  notes?: string;
  created: string;
  updated: string;
}

// Tools Module: Notes
export interface NotesRecord {
  id: string;
  user: string;
  title: string;
  content: string; // markdown
  subject?: string;
  tags?: string[];
  color?: string;
  pinned?: boolean;
  created: string;
  updated: string;
}

// Settings Module
export interface UserSettingsRecord {
  id: string;
  user: string;
  theme: 'light' | 'dark' | 'auto';
  language: 'de' | 'en' | 'nl' | 'es' | 'fr';
  notifications: {
    email?: boolean;
    push?: boolean;
    reminders?: boolean;
  };
  offlineMode?: boolean;
  autoSync?: boolean;
  created: string;
  updated: string;
}

/**
 * Union type for all records (useful for generic operations)
 */
export type AnyRecord =
  | UserRecord
  | BodyDataRecord
  | ExerciseLogsRecord
  | DailyGoalsRecord
  | CalendarEventsRecord
  | TimetableRecord
  | HomeworkRecord
  | ExamsRecord
  | GradesRecord
  | CustomSubjectsRecord
  | SubjectFilesRecord
  | FlashcardDecksRecord
  | FlashcardsRecord
  | NotesRecord
  | UserSettingsRecord;

/**
 * Collection name mappings for compile-time safety
 */
export const COLLECTIONS = {
  users: 'users',
  body_data: 'body_data',
  exercise_logs: 'exercise_logs',
  daily_goals: 'daily_goals',
  calendar_events: 'calendar_events',
  timetable: 'timetable',
  homework: 'homework',
  exams: 'exams',
  grades: 'grades',
  custom_subjects: 'custom_subjects',
  subject_files: 'subject_files',
  flashcard_decks: 'flashcard_decks',
  flashcards: 'flashcards',
  notes: 'notes',
  user_settings: 'user_settings',
} as const;

export type CollectionKey = keyof typeof COLLECTIONS;
